import React, { useEffect, useState } from 'react';
import BranchCard from '../components/mobile/Branch';
import BranchHeader from '../components/common/Branch';
import BranchTable from '../components/desktop/Branch';
import { createBranch, getBranches, toggleBranchStatus, updateBranchPartial } from '../services/branches';
import Spinner from '../components/ui/Spinner';
import Pagination from '../components/Pagination';
import BranchForm from '../components/form/Branch';
import ConfirmationModal from '../components/modals/ConfirmationModal';

const Branches = () => {

  const [branches, setBranches] = useState([]);
  const [count, setCount] = useState(0);
  const [editingBranch, setEditingBranch] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(null);
  const [filter, setFilter] = useState('Active');
  const [confirmModalParams, setConfirmModalParams] = useState({
    open: false,
    title: 'Are you sure?',
    message: 'Do you really want to proceed with this action?',
    confirmText: 'Yes, Do it',
    cancelText: 'No, Cancel',
  });
  const branchesPerPage = 20;

  useEffect(() => {
    setPendingPage(1);
  }, [search, filter]);

  useEffect(() => {
    if (pendingPage != null) {
      fetchBranches(pendingPage);
    }
  }, [pendingPage]);

  const fetchBranches = async (page) => {
    setLoading(true);
    const params = { page, search,
      status: filter.toLowerCase(),
      pageSize:branchesPerPage
    };
    await getBranches(params)
      .then(([err, data]) => {
        const rows = Array.isArray(data?.results) ? data.results : [];
        const normalizedFilter = (filter || 'all').toLowerCase();
        const filteredRows = normalizedFilter === 'active'
          ? rows.filter((item) => Boolean(item?.is_active))
          : normalizedFilter === 'inactive'
          ? rows.filter((item) => !Boolean(item?.is_active))
          : rows;
        setBranches(filteredRows);
        setCount(data.count);
        setCurrentPage(page);
        setPendingPage(null);
        setLoading(false);
      })
      .catch((error) => {
        setPendingPage(null);
        setLoading(false);
      });
  };

  const handleAdd = () => {
    setEditingBranch(null);
    setShowForm(true);
    setErrors([]);
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setInitialData(branch);
    setShowForm(true);
    setErrors([]);
  };

  const onSearchTextChange = (e) => {
    setInputSearch(e.target.value);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearch(inputSearch.trim());
  };

  const onFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const getChangedFields = (initial, updated) => {
    const changed = {};
    for (const key in updated) {
      if (Array.isArray(updated[key])) {
        if (JSON.stringify(initial[key]) !== JSON.stringify(updated[key])) {
          changed[key] = updated[key];
        }
      } else if (updated[key] !== initial[key]) {
        changed[key] = updated[key];
      }
    }
    return changed;
  };

  const handleSave = async (branchData) => {
    let err, response;
    setLoading(true);
    try {
      if (editingBranch) {
        const changedFields = getChangedFields(initialData, branchData);
        if (Object.keys(changedFields).length === 0) {
          return;
        }
        [err, response] = await updateBranchPartial(branchData.id, changedFields);
        if (!err) {
          setInitialData({});
        }
      } else {
        [err, response] = await createBranch(branchData);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
    if (err) {
      setErrors(err);
    } else {
      await fetchBranches(currentPage);
      setShowForm(false);
    }
  };

  const handleToggleStatus = async (branch) => {
    setConfirmModalParams({
      ...confirmModalParams,
      open: true,
      title: branch.is_active ? 'Deactivate Branch' : 'Activate Branch',
      message: `Are you sure you want to ${branch.is_active ? 'deactivate' : 'activate'} the branch "${branch.name}"?`,
      onConfirm: () => handleAction(branch),
    });
  };

  const handleAction = async (branch) => {
    setLoading(true);
    await toggleBranchStatus(branch.id, !branch.is_active);
    setConfirmModalParams({...confirmModalParams, open: false});
    await fetchBranches(currentPage);
    setLoading(false);
  };

  const onClearSearch = () => {
    setInputSearch('');
    setSearch('');
    setCurrentPage(1);
    setPendingPage(1);
  };

  const handleBranchSelection = (branch) => {
    setEditingBranch(branch);
    setShowForm(true);
  };

  const handleCancel = async () => {
    setShowForm(false);
  };

  return (
    <div className="relative p-4 w-full">
      <BranchHeader
        inputSearch={inputSearch}
        filter={filter}
        count={count}
        showForm={showForm}
        onSearchSubmit={onSearchSubmit}
        onSearchTextChange={onSearchTextChange}
        onBack={handleCancel}
        onFilterChange={onFilterChange}
        handleAdd={handleAdd}
        onClearSearch={onClearSearch}
        branch={editingBranch}
      />
      {showForm ? (
        <BranchForm
          branch={editingBranch}
          loading={loading}
          dataErrors={errors}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      ) : (
        <>
          {loading ? <Spinner /> : (
            <>
            <BranchCard
                branches={branches}
                handleEdit={handleEdit}
                handleToggleStatus={handleToggleStatus}
            />
            <BranchTable
                branches={branches}
                handleEdit={handleEdit}
                handleToggleStatus={handleToggleStatus}
                onBranchSelected={handleBranchSelection}
            />
            {/* ✅ Pagination only if users exist */}
            <div className="md:static fixed bottom-0 left-0 w-full bg-white border-t shadow z-10">
                {branches.length > 0 && (
                    <Pagination
                    currentPage={currentPage}
                    totalPages={Math.max(1, Math.ceil(count / branchesPerPage))}
                    onPageChange={setPendingPage}
                    />
                )}
            </div>
            </>
          )}
        </>
      )}
      <ConfirmationModal
        open={confirmModalParams.open}
        title={confirmModalParams.title}
        message={confirmModalParams.message}
        onConfirm={confirmModalParams.onConfirm}
        onCancel={() => setConfirmModalParams({...confirmModalParams, open: false})}
        confirmText={confirmModalParams.confirmText}
        cancelText={confirmModalParams.cancelText}
      />
    </div>
  );
};

export default Branches;
